import { useEffect, useMemo, useRef, useState } from "react";
import type { ReportKind, ReportingResult } from "../../shared/reporting";
import { useSession } from "../auth/session";
import { useWorkspaceAccountId } from "../components/AccountStatus";
import { useLocationPath } from "../routing/useLocationPath";

class ReportAccessError extends Error {}

export async function requestReport<K extends ReportKind>(getToken: () => Promise<string | null>, kind: K, query: string, signal: AbortSignal): Promise<Extract<ReportingResult, {kind: K}>> {
  const token = await getToken();
  if (!token) throw new ReportAccessError("Your session is unavailable. Sign in and try again.");
  const response = await fetch(`/api/reports/${kind}?${query}`, {signal, headers: {authorization: `Bearer ${token}`}});
  if (!response.ok) {
    const body = await response.json().catch(() => null) as {error?: string} | null;
    const message = body?.error ?? "This report is temporarily unavailable. Try again.";
    throw response.status === 401 || response.status === 403 ? new ReportAccessError(message) : new Error(message);
  }
  return response.json();
}
interface Snapshot<T> { scope: string; completedKey: symbol | null; settledKey: symbol | null; settledQuery: string; query: string; result: T | null; error: string }
function useReportRequest<T>(identity: string, query: string, enabled: boolean, retainPrevious: boolean, load: (token: () => Promise<string | null>, signal: AbortSignal) => Promise<T>) {
  const {getToken, state, sessionId, userId} = useSession(), account = useWorkspaceAccountId(), path = useLocationPath();
  const token = useRef(getToken); token.current = getToken;
  const loader = useRef(load); loader.current = load;
  const [attempt, retry] = useState(0);
  const scope = JSON.stringify([identity, path, state, sessionId, userId, account, enabled]);
  // Each visit/retry is a distinct request, even A → B → A while B is pending.
  const key = useMemo(() => Symbol("report request"), [scope, query, attempt]), active = useRef(key); active.current = key;
  const [value, setValue] = useState<Snapshot<T>>({scope, completedKey: null, settledKey: null, settledQuery: "", query, result: null, error: ""});
  useEffect(() => {
    // Identity changes erase the retained snapshot; no data is stored outside
    // this mounted report, and the render below hides mismatched scopes first.
    setValue(previous => previous.scope === scope ? previous : {scope, completedKey: null, settledKey: null, settledQuery: "", query, result: null, error: ""});
    if (!enabled || state !== "signed-in") return;
    const controller = new AbortController();
    void loader.current(token.current, controller.signal).then(result => {
      if (!controller.signal.aborted && active.current === key) setValue({scope, completedKey: key, settledKey: key, settledQuery: query, query, result, error: ""});
    }).catch(failure => {
      // An authoritative access denial also discards same-owner retained data.
      if (!controller.signal.aborted && active.current === key) setValue(previous => ({...previous, ...(failure instanceof ReportAccessError ? {result: null, completedKey: null} : {}), scope, settledKey: key, settledQuery: query, error: failure instanceof Error ? failure.message : "Report unavailable."}));
    });
    return () => controller.abort();
  }, [key]);
  const sameScope = value.scope === scope && enabled && state === "signed-in";
  const result = sameScope && (retainPrevious || value.completedKey === key) ? value.result : null;
  const loading = enabled && state === "signed-in" && (!sameScope || value.settledKey !== key);
  const error = sameScope && (value.settledKey === key || retainPrevious && loading && value.settledQuery === query) ? value.error : "";
  return {result, error, loading, refreshing: loading && Boolean(result), current: Boolean(result) && !loading && !error && value.completedKey === key, resultQuery: result ? value.query : query, retry: () => retry(value => value + 1)};
}
// Retention is explicit for the scoped reporting views. Existing Home consumers
// retain their established hide-on-query/retry behavior.
export function useReportResult<K extends ReportKind>(kind: K, query: string, enabled = true, retainPrevious = false) {
  return useReportRequest(JSON.stringify(kind), query, enabled, retainPrevious, (token, signal) => requestReport(token, kind, query, signal));
}
export type ReportResults<K extends ReportKind> = {[P in K]: Extract<ReportingResult, {kind: P}>};
export function useReportResults<K extends ReportKind>(kinds: readonly K[], query: string, enabled: boolean, validate: (results: ReportResults<K>) => void) {
  return useReportRequest(JSON.stringify(kinds), query, enabled, true, async (token, signal) => {
    const reports = await Promise.all(kinds.map(kind => requestReport(token, kind, query, signal)));
    const result = Object.fromEntries(reports.map(report => [report.kind, report])) as unknown as ReportResults<K>;
    validate(result); return result;
  });
}
